// Vista global de "Gestió laboral -> Control Horari" (CURRENT_TASK.md
// §34-36): supervisió de tots els Empleats (qui ha entrat, qui està
// treballant/en pausa, qui ha finalitzat, qui té pendents offline) per a
// una Data concreta, més una comparativa en paral·lel de diversos Empleats
// seleccionats per a un període (§36) reutilitzant la mateixa resolució
// d'Empleat + Data que la vista individual (§37): no és un simple calendari
// massiu, permet filtrar i comparar.

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { fetchAutenticat, SessioCaducadaError } from "../../utils/auth";
import { formatDate, formatTime, formatNomPersona } from "../../utils/Utilitats";
import { API_URL } from "../../utils/config";
import AjudaModal from "./AjudaModal";
import "../dashboard_v2.css";
import "./gestiolaboralcontrolhorari.css";

const ETIQUETA_ESTAT = {
  SENSE_JORNADA_INICIADA: { text: "Sense entrar", classe: "chg-estat-sense" },
  TREBALLANT: { text: "Treballant", classe: "chg-estat-treballant" },
  EN_PAUSA: { text: "En pausa", classe: "chg-estat-pausa" },
  JORNADA_FINALITZADA: { text: "Finalitzat", classe: "chg-estat-finalitzat" },
};

function dataISOLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dia}`;
}

function avuiISO() {
  return dataISOLocal(new Date());
}

function fa7DiesISO() {
  const d = new Date();
  d.setDate(d.getDate() - 6);
  return dataISOLocal(d);
}

function formatMinuts(minuts) {
  if (minuts === null || minuts === undefined) return "—";
  const signe = minuts < 0 ? "-" : "+";
  const abs = Math.abs(minuts);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `${signe}${h}h${String(m).padStart(2, "0")}`;
}

function AjudaControlHorariGlobal({ onTancar }) {
  return (
    <AjudaModal
      id="chg-ajuda-title"
      titol="Com funciona el Control Horari"
      lead="Supervisió de fitxatges de tota l'empresa per a un dia, i comparativa entre empleats."
      onTancar={onTancar}
    >
      <section className="dv2-help-section">
        <h4>Per a què serveix</h4>
        <p>
          Veure, per a un dia concret, qui no ha entrat, qui treballa, qui està en pausa, qui ha acabat i qui té
          registres pendents offline. No és el portal mòbil de fitxar: aquí només es supervisa.
        </p>
        <p>
          El detall i les correccions d'un empleat van a <strong>Gestió laboral · Empleats</strong> → Gestió → Control
          Horari.
        </p>
      </section>
      <section className="dv2-help-section">
        <h4>Filtres</h4>
        <ul>
          <li>
            <strong>Data</strong>: per defecte avui. Canviar-la recarrega el resum.
          </li>
          <li>
            <strong>Actius / cerca</strong> filtren la taula.
          </li>
        </ul>
      </section>
      <section className="dv2-help-section">
        <h4>Comparativa</h4>
        <p>
          Marca com a mínim dos empleats a la taula, tria <strong>Des de / Fins a</strong> (per defecte els darrers 7
          dies) i prem <strong>Comparar</strong>. Es veuen les jornades en paral·lel: horari real, pauses i desviació
          respecte del previst. Si no hi ha jornada, es mostra el motiu (festiu, baixa, absència, fora de vigència…).
        </p>
      </section>
    </AjudaModal>
  );
}

function GestioLaboralControlHorari() {
  const { rolUsuari, empresaSeleccionada } = useUserContext();
  const potAccedir = tincPermis(rolUsuari, "ACCES", "EMPLEATS");
  const idEmpresa = empresaSeleccionada?.IdEmpresa;

  const [data, setData] = useState(avuiISO());
  const [mostrarActius, setMostrarActius] = useState(true);
  const [filtreText, setFiltreText] = useState("");
  const [mostraAjuda, setMostraAjuda] = useState(false);

  const [resum, setResum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [seleccionats, setSeleccionats] = useState(new Set());

  const [dataIniciComp, setDataIniciComp] = useState(fa7DiesISO());
  const [dataFinalComp, setDataFinalComp] = useState(avuiISO());
  const [comparativa, setComparativa] = useState(null);
  const [loadingComp, setLoadingComp] = useState(false);
  const [errorComp, setErrorComp] = useState("");

  const carregarResum = useCallback(async () => {
    if (!idEmpresa) return;
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ idEmpresa, data, mostrarActius: mostrarActius ? "1" : "0" });
      const res = await fetchAutenticat(`${API_URL}/ControlHorariResumGlobal.php?${params.toString()}`);
      const dades = await res.json();
      if (dades.success) {
        setResum(dades);
      } else {
        setError(dades.message || "No s'ha pogut carregar el resum de Control Horari.");
        setResum(null);
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) setError("Error de connexió amb el servidor.");
    } finally {
      setLoading(false);
    }
  }, [idEmpresa, data, mostrarActius]);

  useEffect(() => {
    carregarResum();
  }, [carregarResum]);

  const empleatsFiltrats = useMemo(() => {
    const text = filtreText.trim().toLowerCase();
    const llista = resum?.empleats || [];
    if (!text) return llista;
    return llista.filter((e) => `${e.Cognom1 || ""} ${e.Cognom2 || ""} ${e.Nom || ""}`.toLowerCase().includes(text));
  }, [resum, filtreText]);

  useEffect(() => {
    setSeleccionats((prev) => {
      const visibles = new Set(empleatsFiltrats.map((e) => e.idEmpleats));
      const nou = new Set([...prev].filter((id) => visibles.has(id)));
      return nou.size === prev.size ? prev : nou;
    });
  }, [empleatsFiltrats]);

  const alternarSeleccio = (idEmpleats) => {
    setSeleccionats((prev) => {
      const nou = new Set(prev);
      if (nou.has(idEmpleats)) nou.delete(idEmpleats);
      else nou.add(idEmpleats);
      return nou;
    });
  };

  const handleComparar = async () => {
    if (seleccionats.size < 2) return;
    if (dataIniciComp > dataFinalComp) {
      setErrorComp("La Data d'inici no pot ser posterior a la Data final.");
      return;
    }
    setLoadingComp(true);
    setErrorComp("");
    try {
      const params = new URLSearchParams({
        idEmpresa,
        idsEmpleats: [...seleccionats].join(","),
        dataInici: dataIniciComp,
        dataFinal: dataFinalComp,
      });
      const res = await fetchAutenticat(`${API_URL}/ControlHorariComparativa.php?${params.toString()}`);
      const dades = await res.json();
      if (dades.success) {
        setComparativa(dades);
      } else {
        setErrorComp(dades.message || "No s'ha pogut carregar la comparativa.");
        setComparativa(null);
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) setErrorComp("Error de connexió amb el servidor.");
    } finally {
      setLoadingComp(false);
    }
  };

  const renderCellaDia = (dia) => {
    if (!dia) return "—";
    if (dia.MotiuSenseHorari) {
      const color = dia.ForaVigencia
        ? "#9e9e9e"
        : dia.Baixa
        ? dia.Baixa.TipusColor || "#999"
        : dia.AbsenciaCompleta
        ? dia.AbsenciaCompleta.TipusColor || "#999"
        : dia.TipusFestiu === "EMPRESA"
        ? "#0099CC"
        : "#607d8b";
      return (
        <span className="chg-xip" style={{ backgroundColor: color }}>
          {dia.MotiuSenseHorari}
        </span>
      );
    }
    if (!dia.fullDiari) return "—";
    return (
      <div className="chg-cella-jornada">
        <span>
          {formatTime(dia.fullDiari.HoraEntrada)}–{dia.fullDiari.HoraSortida ? formatTime(dia.fullDiari.HoraSortida) : "en curs"}
        </span>
        {dia.numPauses > 0 && (
          <span className="chg-cella-pauses">
            {dia.numPauses} pausa{dia.numPauses > 1 ? "es" : ""}
          </span>
        )}
        {dia.comput.tancada && (
          <span className={dia.comput.desviacioMinuts < 0 ? "chg-desviacio-neg" : dia.comput.desviacioMinuts > 0 ? "chg-desviacio-pos" : ""}>
            {formatMinuts(dia.comput.desviacioMinuts)}
          </span>
        )}
      </div>
    );
  };

  if (!potAccedir) {
    return (
      <div className="dv2 dv2-page">
        <p className="dv2-empty">No tens permisos per accedir a aquesta pantalla.</p>
      </div>
    );
  }

  const comptadors = resum?.comptadors;
  const diesComparativa = comparativa?.empleats?.[0]?.dies || [];

  return (
    <div className="dv2 dv2-page dv2-page--fill chg-page">
      {mostraAjuda && <AjudaControlHorariGlobal onTancar={() => setMostraAjuda(false)} />}

      <div className="dv2-pageHeader">
        <div className="dv2-pageHeader-main">
          <div className="dv2-pageHeader-icon" aria-hidden="true">
            ⏱️
          </div>
          <div>
            <h1 className="dv2-pageHeader-title">Gestió laboral · Control Horari</h1>
            <p className="dv2-pageHeader-subtitle">Supervisió de fitxatges de l'empresa activa.</p>
          </div>
        </div>
        <div className="dv2-pageHeader-actions">
          <button
            type="button"
            className="dv2-btn dv2-btn--icon dv2-btn--ghost"
            title="Com funciona aquesta pantalla"
            aria-label="Ajuda"
            onClick={() => setMostraAjuda(true)}
          >
            ?
          </button>
        </div>
      </div>

      <div className="dv2-listBody">
        <div className="dv2-filterBar">
          <label className="dv2-filter">
            <span className="dv2-filter-label">Data</span>
            <input type="date" className="dv2-input" value={data} onChange={(e) => setData(e.target.value)} />
          </label>
          <label className="dv2-filter dv2-filter--toggle">
            <span className="dv2-filter-label">Actius</span>
            <span className="dv2-toggleRow">
              <input type="checkbox" checked={mostrarActius} onChange={(e) => setMostrarActius(e.target.checked)} />
            </span>
          </label>
          <div className="dv2-filter dv2-filter--grow">
            <span className="dv2-filter-label">Cerca</span>
            <input
              type="text"
              className="dv2-input dv2-input-search"
              placeholder="Cerca per nom..."
              value={filtreText}
              onChange={(e) => setFiltreText(e.target.value)}
            />
          </div>
        </div>

        {loading && <p className="dv2-hint">Carregant Control Horari...</p>}
        {error && <p className="chg-error">{error}</p>}

        {!loading && !error && (
          <>
            {comptadors && (
              <div className="chg-comptadors">
                <span className="chg-comptador">
                  Sense entrar: <strong>{comptadors.senseEntrar}</strong>
                </span>
                <span className="chg-comptador">
                  Treballant: <strong>{comptadors.treballant}</strong>
                </span>
                <span className="chg-comptador">
                  En pausa: <strong>{comptadors.enPausa}</strong>
                </span>
                <span className="chg-comptador">
                  Finalitzat: <strong>{comptadors.finalitzat}</strong>
                </span>
                <span className="chg-comptador">
                  Amb pendents offline: <strong>{comptadors.ambPendentsOffline}</strong>
                </span>
              </div>
            )}

            {empleatsFiltrats.length > 0 ? (
              <div className="dv2-tableWrap">
                <table className="dv2-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Empleat</th>
                      <th>Estat</th>
                      <th>Entrada</th>
                      <th>Sortida</th>
                      <th>Pauses</th>
                      <th>Offline</th>
                    </tr>
                  </thead>
                  <tbody>
                    {empleatsFiltrats.map((emp) => {
                      const nomComplet = formatNomPersona(emp);
                      const etiqueta = ETIQUETA_ESTAT[emp.estatActual] || { text: emp.estatActual, classe: "" };
                      return (
                        <tr key={emp.idEmpleats}>
                          <td>
                            <input
                              type="checkbox"
                              checked={seleccionats.has(emp.idEmpleats)}
                              onChange={() => alternarSeleccio(emp.idEmpleats)}
                            />
                          </td>
                          <td>
                            {nomComplet || "—"}
                            {emp.Actiu === 0 && <em> (Inactiu)</em>}
                          </td>
                          <td>
                            <span className={`chg-xip-estat ${etiqueta.classe}`}>{etiqueta.text}</span>
                          </td>
                          <td>{emp.HoraEntrada ? formatTime(emp.HoraEntrada) : "—"}</td>
                          <td>{emp.HoraSortida ? formatTime(emp.HoraSortida) : "—"}</td>
                          <td>{emp.numPauses}</td>
                          <td>{emp.tePendentsOffline ? <span className="chg-xip chg-xip-offline">Offline</span> : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="dv2-empty">No s'ha trobat cap Empleat amb aquests filtres.</p>
            )}

            <div className="chg-comparativa-section">
              <h4 className="chg-comparativa-titol">Comparativa d'empleats seleccionats</h4>
              <p className="chg-comparativa-ajuda">
                Selecciona com a mínim 2 empleats de la taula ({seleccionats.size} seleccionat
                {seleccionats.size === 1 ? "" : "s"}) per veure les seves jornades en paral·lel.
              </p>
              <div className="chg-comparativa-filtres">
                <label className="dv2-filter">
                  <span className="dv2-filter-label">Des de</span>
                  <input type="date" className="dv2-input" value={dataIniciComp} onChange={(e) => setDataIniciComp(e.target.value)} />
                </label>
                <label className="dv2-filter">
                  <span className="dv2-filter-label">Fins a</span>
                  <input type="date" className="dv2-input" value={dataFinalComp} onChange={(e) => setDataFinalComp(e.target.value)} />
                </label>
                <button
                  type="button"
                  className="dv2-btn dv2-btn--primary"
                  onClick={handleComparar}
                  disabled={seleccionats.size < 2 || loadingComp}
                >
                  Comparar
                </button>
              </div>

              {errorComp && <p className="chg-error">{errorComp}</p>}
              {loadingComp && <p className="dv2-hint">Carregant comparativa...</p>}

              {comparativa && !loadingComp && (
                <div className="dv2-tableWrap">
                  <table className="dv2-table chg-taula-comparativa">
                    <thead>
                      <tr>
                        <th>Data</th>
                        {comparativa.empleats.map((emp) => (
                          <th key={emp.idEmpleats}>{emp.nomComplet}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {diesComparativa.map((_, idx) => {
                        const primeraData = comparativa.empleats[0].dies[idx].Data;
                        return (
                          <tr key={primeraData}>
                            <td>{formatDate(primeraData)}</td>
                            {comparativa.empleats.map((emp) => (
                              <td key={emp.idEmpleats}>{renderCellaDia(emp.dies[idx])}</td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default GestioLaboralControlHorari;
